/**
 * Settings Persistence Layer
 * Handles loading and saving settings to JSON files in AppData
 * According to architecture: Users\AppData\Roaming\Carousel
 */
import { getAgentServices } from '../services';
import { DEFAULT_MODEL, VALID_MODEL_IDS } from '@carousel-office/shared';
import { backendSettingsService, DEFAULT_BACKEND_CUSTOM_INSTRUCTIONS, } from '@carousel-office/shared';
function createEmptyCustomInstructions() {
    return {
        carouselChat: [],
        search: [],
        research: [],
        execution: [],
    };
}
function normalizeCustomInstructions(value) {
    const source = value || {};
    const toArray = (items) => {
        if (!Array.isArray(items)) {
            return [];
        }
        return items.filter(Boolean);
    };
    return {
        carouselChat: toArray(source.carouselChat),
        search: toArray(source.search),
        research: toArray(source.research),
        execution: toArray(source.execution),
    };
}
function cloneCustomInstructions(value) {
    return {
        carouselChat: [...(value.carouselChat || [])],
        search: [...(value.search || [])],
        research: [...(value.research || [])],
        execution: [...(value.execution || [])],
    };
}
function toBackendCustomInstructions(value) {
    const normalized = normalizeCustomInstructions(value);
    return {
        carouselChat: [...normalized.carouselChat],
        search: [...(normalized.search || [])],
        research: [...(normalized.research || [])],
        execution: [...(normalized.execution || [])],
    };
}
function fromBackendCustomInstructions(value) {
    if (!value) {
        return createEmptyCustomInstructions();
    }
    return normalizeCustomInstructions({
        carouselChat: value.carouselChat || [],
        search: value.search || [],
        research: value.research || [],
        execution: value.execution || [],
    });
}
function isPromptHost(value) {
    return value === "powerpoint" || value === "excel";
}
function normalizeSavedPrompt(value, fallbackHost) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return null;
    }
    const source = value;
    const id = typeof source.id === "string" ? source.id.trim() : "";
    const title = typeof source.title === "string" ? source.title : "";
    const content = typeof source.content === "string" ? source.content : "";
    const host = isPromptHost(source.host) ? source.host : fallbackHost ?? null;
    if (!id || !title.trim() || !content.trim() || !host) {
        return null;
    }
    return {
        id,
        title,
        content,
        host,
    };
}
function normalizeSavedPromptList(value, fallbackHost) {
    if (!Array.isArray(value)) {
        return [];
    }
    const prompts = [];
    for (const item of value) {
        // Hard-cut behavior: prompts without a valid host are ignored.
        const normalized = normalizeSavedPrompt(item, fallbackHost);
        if (normalized) {
            prompts.push(normalized);
        }
    }
    return prompts;
}
/**
 * Settings persistence manager
 * TypeScript-as-leader pattern: TypeScript manages settings, syncs to Python
 */
class SettingsPersistence {
    constructor() {
        this.isInitialized = false;
        // Default settings matching architecture defaults
        this.settings = {
            settings: {
                llm: {
                    adapter: "keywords",
                },
                modelSettings: {
                    chatModel: DEFAULT_MODEL,
                    reasoningMode: "off",
                },
                excelExport: {
                    editMode: "clone", // Safe default: edit clones
                    defaultBlackAndWhite: true, // Clean exports by default
                    defaultCollapseGroups: true, // Collapse groups by default
                    defaultClearComments: true, // Remove comments by default
                },
                general: {
                    conversationRetentionDays: 7, // Default: keep for 1 week
                    liveSlideIndexingEnabled: true,
                },
                experimental: {
                    spacePlanningMode: "analysisFirst", // Default: Idea A (LLM → Gemini)
                },
            },
            customInstructions: createEmptyCustomInstructions(),
            customInstructionsByMode: {
                powerpoint: fromBackendCustomInstructions(DEFAULT_BACKEND_CUSTOM_INSTRUCTIONS),
                excel: fromBackendCustomInstructions(DEFAULT_BACKEND_CUSTOM_INSTRUCTIONS),
            },
            promptLibrary: [],
        };
    }
    static getInstance() {
        if (!SettingsPersistence.instance) {
            SettingsPersistence.instance = new SettingsPersistence();
        }
        return SettingsPersistence.instance;
    }
    getCurrentSettingsMode() {
        const configuredHost = getAgentServices().hostType;
        if (configuredHost === "excel" || configuredHost === "powerpoint") {
            return configuredHost;
        }
        try {
            const officeHost = String(globalThis?.Office?.context?.host || "").toLowerCase();
            if (officeHost.includes("excel")) {
                return "excel";
            }
        }
        catch {
            // Fall back to PowerPoint mode when host detection is unavailable.
        }
        return "powerpoint";
    }
    ensureCustomInstructionModes() {
        const legacy = normalizeCustomInstructions(this.settings.customInstructions);
        const existingByMode = this.settings.customInstructionsByMode;
        const normalizedByMode = {
            powerpoint: normalizeCustomInstructions(existingByMode?.powerpoint || legacy),
            excel: normalizeCustomInstructions(existingByMode?.excel || legacy),
        };
        this.settings.customInstructionsByMode = normalizedByMode;
        this.settings.customInstructions = cloneCustomInstructions(normalizedByMode[this.getCurrentSettingsMode()]);
    }
    getModeCustomInstructions(mode = this.getCurrentSettingsMode()) {
        this.ensureCustomInstructionModes();
        return cloneCustomInstructions(this.settings.customInstructionsByMode[mode]);
    }
    setModeCustomInstructions(mode, instructions) {
        this.ensureCustomInstructionModes();
        this.settings.customInstructionsByMode[mode] = normalizeCustomInstructions(instructions);
        if (mode === this.getCurrentSettingsMode()) {
            this.settings.customInstructions = cloneCustomInstructions(this.settings.customInstructionsByMode[mode]);
        }
    }
    async persistLocalSettingsOnly() {
        if (typeof Office !== 'undefined' && Office.context?.document?.settings) {
            await this.saveToOfficeStorage();
        }
        else {
            this.saveToLocalStorage();
        }
    }
    async hydrateCurrentModeCustomInstructionsFromBackend() {
        const mode = this.getCurrentSettingsMode();
        try {
            await backendSettingsService.flushPendingWrites();
            const backendInstructions = await backendSettingsService.getCustomInstructions(mode);
            this.setModeCustomInstructions(mode, fromBackendCustomInstructions(backendInstructions));
            await this.persistLocalSettingsOnly();
            console.log(`[SettingsPersistence] Hydrated custom instructions from backend (${mode})`);
        }
        catch (error) {
            console.warn(`[SettingsPersistence] Backend custom instructions hydrate failed (${mode}):`, error);
        }
    }
    async syncCurrentModeCustomInstructionsToBackend() {
        const mode = this.getCurrentSettingsMode();
        const current = this.getModeCustomInstructions(mode);
        try {
            await backendSettingsService.putCustomInstructions(mode, toBackendCustomInstructions(current));
        }
        catch (error) {
            console.warn(`[SettingsPersistence] Backend custom instructions sync deferred (${mode}):`, error);
        }
    }
    /**
     * Initialize settings persistence
     * In Office.js context, we use Office.js storage
     * In Node.js context (Electron), we use file system
     */
    async initialize() {
        if (this.isInitialized) {
            return;
        }
        try {
            // Try to load from Office.js storage first
            if (typeof Office !== 'undefined' && Office.context?.document?.settings) {
                await this.loadFromOfficeStorage();
            }
            else {
                // Fallback: try to load from localStorage (for development/testing)
                this.loadFromLocalStorage();
            }
            this.ensureCustomInstructionModes();
            await this.hydrateCurrentModeCustomInstructionsFromBackend();
            this.isInitialized = true;
            console.log('[SettingsPersistence] Initialized successfully');
        }
        catch (error) {
            console.error('[SettingsPersistence] Error initializing:', error);
            // Use defaults if loading fails
            this.isInitialized = true;
        }
    }
    /**
     * Load settings from Office.js storage
     */
    async loadFromOfficeStorage() {
        try {
            const settings = Office.context.document.settings;
            const settingsJson = settings.get('carousel_settings');
            if (settingsJson) {
                this.settings = JSON.parse(settingsJson);
                console.log('[SettingsPersistence] Loaded from Office storage');
            }
        }
        catch (error) {
            console.error('[SettingsPersistence] Error loading from Office storage:', error);
        }
    }
    /**
     * Load settings from localStorage (fallback)
     */
    loadFromLocalStorage() {
        try {
            const settingsJson = localStorage.getItem('carousel_settings');
            if (settingsJson) {
                this.settings = JSON.parse(settingsJson);
                console.log('[SettingsPersistence] Loaded from localStorage');
            }
        }
        catch (error) {
            console.error('[SettingsPersistence] Error loading from localStorage:', error);
        }
    }
    /**
     * Save settings to storage
     */
    async save() {
        this.settings.lastUpdated = new Date().toISOString();
        try {
            // Try Office.js storage first
            if (typeof Office !== 'undefined' && Office.context?.document?.settings) {
                await this.saveToOfficeStorage();
            }
            else {
                // Fallback to localStorage
                this.saveToLocalStorage();
            }
            // Sync to Python backend (if available)
            await this.syncToPython();
        }
        catch (error) {
            console.error('[SettingsPersistence] Error saving settings:', error);
            throw error;
        }
    }
    /**
     * Save to Office.js storage
     */
    async saveToOfficeStorage() {
        const settings = Office.context.document.settings;
        settings.set('carousel_settings', JSON.stringify(this.settings));
        await settings.saveAsync();
        console.log('[SettingsPersistence] Saved to Office storage');
    }
    /**
     * Save to localStorage (fallback)
     */
    saveToLocalStorage() {
        localStorage.setItem('carousel_settings', JSON.stringify(this.settings));
        console.log('[SettingsPersistence] Saved to localStorage');
    }
    /**
     * Sync settings to Python backend
     * Architecture: TypeScript-as-leader sends updates to Python via sync endpoint
     * Uses centralized BackendClient for consistent URL configuration.
     */
    async syncToPython() {
        const bc = getAgentServices().backendClient;
        if (bc?.syncSettings)
            await bc.syncSettings(this.settings);
    }
    /**
     * Get current settings
     */
    getSettings() {
        return { ...this.settings };
    }
    /**
     * Get user settings
     */
    getUserSettings() {
        return { ...this.settings.settings };
    }
    /**
     * Get custom instructions
     */
    getCustomInstructions() {
        return cloneCustomInstructions(this.getModeCustomInstructions());
    }
    /**
     * Get custom instructions for a specific category
     */
    getCustomInstructionsForCategory(category) {
        const customInstructions = this.getModeCustomInstructions();
        return [...(customInstructions[category] || [])];
    }
    /**
     * Get active custom instructions for a category (only those with on=true)
     */
    getActiveCustomInstructions(category) {
        return this.getCustomInstructionsForCategory(category).filter(i => i.on);
    }
    /**
     * Get concatenated active instructions as a single string (for LLM injection)
     */
    getActiveInstructionsText(category) {
        const active = this.getActiveCustomInstructions(category);
        if (active.length === 0)
            return '';
        return active.map(i => `<custom_instruction title="${i.title}">\n${i.instruction}\n</custom_instruction>`).join('\n\n');
    }
    /**
     * Update user settings
     */
    async updateSettings(settings) {
        this.settings.settings = {
            ...this.settings.settings,
            ...settings,
        };
        await this.save();
    }
    /**
     * Update custom instructions for a category
     */
    async updateCustomInstructions(category, instructions) {
        const mode = this.getCurrentSettingsMode();
        const customInstructions = this.getModeCustomInstructions(mode);
        customInstructions[category] = instructions;
        this.setModeCustomInstructions(mode, customInstructions);
        await this.save();
        await this.syncCurrentModeCustomInstructionsToBackend();
    }
    /**
     * Add a new custom instruction to a category
     */
    async addCustomInstruction(category, instruction) {
        const newInstruction = {
            ...instruction,
            id: crypto.randomUUID(),
        };
        const mode = this.getCurrentSettingsMode();
        const customInstructions = this.getModeCustomInstructions(mode);
        if (!customInstructions[category]) {
            customInstructions[category] = [];
        }
        customInstructions[category].push(newInstruction);
        this.setModeCustomInstructions(mode, customInstructions);
        await this.save();
        await this.syncCurrentModeCustomInstructionsToBackend();
        return newInstruction;
    }
    /**
     * Update a specific custom instruction
     */
    async updateCustomInstruction(category, id, updates) {
        const mode = this.getCurrentSettingsMode();
        const customInstructions = this.getModeCustomInstructions(mode);
        const instructions = customInstructions[category];
        if (!instructions)
            return;
        const index = instructions.findIndex(i => i.id === id);
        if (index === -1)
            return;
        instructions[index] = { ...instructions[index], ...updates };
        this.setModeCustomInstructions(mode, customInstructions);
        await this.save();
        await this.syncCurrentModeCustomInstructionsToBackend();
    }
    /**
     * Delete a custom instruction
     */
    async deleteCustomInstruction(category, id) {
        const mode = this.getCurrentSettingsMode();
        const customInstructions = this.getModeCustomInstructions(mode);
        const instructions = customInstructions[category];
        if (!instructions)
            return;
        const index = instructions.findIndex(i => i.id === id);
        if (index !== -1) {
            instructions.splice(index, 1);
            this.setModeCustomInstructions(mode, customInstructions);
            await this.save();
            await this.syncCurrentModeCustomInstructionsToBackend();
        }
    }
    /**
     * Toggle a custom instruction on/off
     */
    async toggleCustomInstruction(category, id) {
        const mode = this.getCurrentSettingsMode();
        const customInstructions = this.getModeCustomInstructions(mode);
        const instructions = customInstructions[category];
        if (!instructions)
            return;
        const instruction = instructions.find(i => i.id === id);
        if (instruction) {
            instruction.on = !instruction.on;
            this.setModeCustomInstructions(mode, customInstructions);
            await this.save();
            await this.syncCurrentModeCustomInstructionsToBackend();
        }
    }
    /**
     * Reset to defaults
     */
    async resetToDefaults() {
        this.settings = {
            settings: {
                llm: {
                    adapter: "keywords",
                },
                modelSettings: {
                    chatModel: DEFAULT_MODEL,
                    reasoningMode: "off",
                },
                excelExport: {
                    editMode: "clone",
                    defaultBlackAndWhite: true,
                    defaultCollapseGroups: true,
                    defaultClearComments: true,
                },
                general: {
                    conversationRetentionDays: 7,
                    liveSlideIndexingEnabled: true,
                },
            },
            customInstructions: createEmptyCustomInstructions(),
            customInstructionsByMode: {
                powerpoint: fromBackendCustomInstructions(DEFAULT_BACKEND_CUSTOM_INSTRUCTIONS),
                excel: fromBackendCustomInstructions(DEFAULT_BACKEND_CUSTOM_INSTRUCTIONS),
            },
            promptLibrary: [],
        };
        await this.save();
    }
    getCurrentPromptHost() {
        return this.getCurrentSettingsMode();
    }
    getNormalizedPromptLibrary() {
        const rawCount = Array.isArray(this.settings.promptLibrary)
            ? this.settings.promptLibrary.length
            : 0;
        const normalized = normalizeSavedPromptList(this.settings.promptLibrary);
        if (!Array.isArray(this.settings.promptLibrary) || normalized.length !== rawCount) {
            this.settings.promptLibrary = normalized;
        }
        return [...normalized];
    }
    // =========================================================================
    // PROMPT LIBRARY
    // =========================================================================
    /**
     * Get all saved prompts
     */
    getPromptLibrary() {
        const currentHost = this.getCurrentPromptHost();
        return this.getNormalizedPromptLibrary().filter((prompt) => prompt.host === currentHost);
    }
    /**
     * Get a specific prompt by ID
     */
    getPromptById(id) {
        return this.getPromptLibrary().find((prompt) => prompt.id === id);
    }
    /**
     * Add a new prompt to the library
     */
    async addPrompt(prompt) {
        const currentHost = this.getCurrentPromptHost();
        const newPrompt = normalizeSavedPrompt({
            ...prompt,
            id: crypto.randomUUID(),
            host: prompt.host ?? currentHost,
        }, currentHost);
        if (!newPrompt) {
            throw new Error("[SettingsPersistence] Invalid prompt payload.");
        }
        const allPrompts = this.getNormalizedPromptLibrary();
        allPrompts.push(newPrompt);
        this.settings.promptLibrary = allPrompts;
        await this.save();
        return newPrompt;
    }
    /**
     * Update an existing prompt
     */
    async updatePrompt(id, updates) {
        const currentHost = this.getCurrentPromptHost();
        const allPrompts = this.getNormalizedPromptLibrary();
        const index = allPrompts.findIndex((prompt) => prompt.id === id && prompt.host === currentHost);
        if (index === -1)
            return;
        const currentPrompt = allPrompts[index];
        const nextPrompt = normalizeSavedPrompt({
            ...currentPrompt,
            ...updates,
            host: currentPrompt.host,
        }, currentPrompt.host);
        if (!nextPrompt) {
            return;
        }
        allPrompts[index] = nextPrompt;
        this.settings.promptLibrary = allPrompts;
        await this.save();
    }
    /**
     * Delete a prompt from the library
     */
    async deletePrompt(id) {
        const currentHost = this.getCurrentPromptHost();
        const allPrompts = this.getNormalizedPromptLibrary();
        const index = allPrompts.findIndex((prompt) => prompt.id === id && prompt.host === currentHost);
        if (index !== -1) {
            allPrompts.splice(index, 1);
            this.settings.promptLibrary = allPrompts;
            await this.save();
        }
    }
    /**
     * Replace all prompts in the library
     */
    async setPromptLibrary(prompts) {
        const currentHost = this.getCurrentPromptHost();
        const allPrompts = this.getNormalizedPromptLibrary();
        const otherHostPrompts = allPrompts.filter((prompt) => prompt.host !== currentHost);
        const currentHostPrompts = normalizeSavedPromptList(prompts, currentHost).map((prompt) => ({
            ...prompt,
            host: currentHost,
        }));
        this.settings.promptLibrary = [...otherHostPrompts, ...currentHostPrompts];
        await this.save();
    }
    /**
     * Get Excel export settings
     */
    getExcelExportSettings() {
        return this.settings.settings.excelExport || {
            editMode: "clone",
            defaultBlackAndWhite: true,
            defaultCollapseGroups: true,
            defaultClearComments: true,
        };
    }
    /**
     * Update Excel export settings
     */
    async updateExcelExportSettings(updates) {
        this.settings.settings.excelExport = {
            ...this.getExcelExportSettings(),
            ...updates,
        };
        await this.save();
    }
    // =========================================================================
    // GENERAL SETTINGS
    // =========================================================================
    /**
     * Get general settings
     */
    getGeneralSettings() {
        const general = this.settings.settings.general || {
            conversationRetentionDays: 7,
            liveSlideIndexingEnabled: true,
        };
        return {
            conversationRetentionDays: general.conversationRetentionDays ?? 7,
            liveSlideIndexingEnabled: general.liveSlideIndexingEnabled ?? true,
        };
    }
    /**
     * Update general settings
     */
    async updateGeneralSettings(updates) {
        this.settings.settings.general = {
            ...this.getGeneralSettings(),
            ...updates,
        };
        await this.save();
        console.log('[SettingsPersistence] General settings updated:', updates);
    }
    /**
     * Get conversation retention days (1-14)
     */
    getConversationRetentionDays() {
        const days = this.getGeneralSettings().conversationRetentionDays;
        // Clamp to valid range
        return Math.max(1, Math.min(14, days));
    }
    /**
     * Set conversation retention days (1-14)
     */
    async setConversationRetentionDays(days) {
        // Clamp to valid range
        const clampedDays = Math.max(1, Math.min(14, days));
        await this.updateGeneralSettings({ conversationRetentionDays: clampedDays });
    }
    // =========================================================================
    // EXPERIMENTAL SETTINGS
    // =========================================================================
    /**
     * Get experimental settings
     */
    getExperimentalSettings() {
        return this.settings.settings.experimental || {
            spacePlanningMode: "analysisFirst",
        };
    }
    /**
     * Update experimental settings
     */
    async updateExperimentalSettings(updates) {
        this.settings.settings.experimental = {
            ...this.getExperimentalSettings(),
            ...updates,
        };
        await this.save();
    }
    /**
     * Get space planning mode for A/B testing
     */
    getSpacePlanningMode() {
        return this.getExperimentalSettings().spacePlanningMode;
    }
    /**
     * Set space planning mode for A/B testing
     */
    async setSpacePlanningMode(mode) {
        await this.updateExperimentalSettings({ spacePlanningMode: mode });
    }
    // =========================================================================
    // MODEL SETTINGS
    // =========================================================================
    /**
     * Get model settings
     */
    getModelSettings() {
        const raw = this.settings.settings.modelSettings || {
            chatModel: DEFAULT_MODEL,
            reasoningMode: "off",
        };
        // Migrate deprecated model IDs (e.g., opus-4-5 → opus-4-6)
        let migrated = false;
        if (!VALID_MODEL_IDS.has(raw.chatModel)) {
            console.log(`[SettingsPersistence] Migrating chatModel "${raw.chatModel}" → "${DEFAULT_MODEL}"`);
            raw.chatModel = DEFAULT_MODEL;
            migrated = true;
        }
        if (migrated) {
            this.settings.settings.modelSettings = raw;
            // Fire-and-forget save for migration
            this.save().catch(() => { });
        }
        return raw;
    }
    /**
     * Update model settings
     */
    async updateModelSettings(updates) {
        this.settings.settings.modelSettings = {
            ...this.getModelSettings(),
            ...updates,
        };
        await this.save();
        console.log('[SettingsPersistence] Model settings updated:', updates);
    }
    /**
     * Get chat model (main orchestrator)
     */
    getChatModel() {
        return this.getModelSettings().chatModel;
    }
    /**
     * Get reasoning mode
     */
    getReasoningMode() {
        return this.getModelSettings().reasoningMode;
    }
}
export default SettingsPersistence;
